import React from 'react';
import PropTypes from 'prop-types';
import { InputSelectField } from '@tempmee/react';
import { useAccessibleName } from '../../../hooks/useAccessibleName';

/**
 * A labelled single-select filter — the one place the app renders a design
 * system `InputSelectField`.
 *
 * Every filter bar had been wiring this up by hand: an `InputSelectField`, a
 * `useAccessibleName` ref beside it, and the same `{ value, label }` option
 * list. Thirteen copies, one of which carried its own inlined duplicate of the
 * hook. The ref is the interesting part and the reason this is a component
 * rather than a convention — the design system spreads no rest props, so
 * `aria-label` cannot reach the trigger and its visible `label` renders as an
 * unassociated span. Forget the ref and the control is simply unnamed for
 * assistive tech, which is invisible in review.
 *
 * Takes and returns values untouched. For the repo's legacy `{ code, name }`
 * option lists with an `'all'` sentinel, use `FilterSelect`, which adapts that
 * contract onto this.
 */
export const SelectFilter = ({ label, options, value, onChange, error, disabled, isLoading }) => {
  const triggerRef = useAccessibleName(label);

  return (
    <InputSelectField
      ref={triggerRef}
      label={label}
      options={options}
      value={value}
      onChange={onChange}
      error={error}
      disabled={disabled}
      isLoading={isLoading}
    />
  );
};

SelectFilter.propTypes = {
  /** Visible caption and the accessible name. */
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
      label: PropTypes.string,
    }),
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  /** Called with the selected option's `value`. */
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
};

SelectFilter.defaultProps = {
  value: undefined,
  error: false,
  disabled: false,
  isLoading: false,
};

export default SelectFilter;
